const container = document.getElementById(`chm-cost-comparison-tool`);
if (container) {
  // React methods natively available within WordPress
  // - https://javascriptforwp.com/adding-react-to-a-wordpress-theme-tutorial/
  const { createRoot, StrictMode, useState, useId, useEffect, useRef } = wp.element;
  const $ = jQuery;

  function CostComparisonTool() {
    /* * * * * * * * * * * * * * * * * * *
     * constants for use throughout tool *
     * * * * * * * * * * * * * * * * * * */
    const _C = {
      // adds USD currency symbol, rounds up with 0 decimals
      CURRENCY_FORMAT: { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 },

      // include all ACF steps plus Calculator as step 4
      NUM_STEPS_TOTAL: 4,

      // CHM Plus cost
      CHM_PLUS_MONTHLY_COST_PER_UNIT: {
        "bronze": 32,
        "silver": 32,
        "gold": 32,
        "seniorshare": 0
      }
    };

    // accessing ACF data, which is localized in the block template
    const { intro_content, step_content, calculator_step_content, results_step_content } = cct_acf_data;

    // accessing hard-coded data for now, from block template
    const { cct_step_options, calculator_initial_settings, calculator_initial_values, cct_calculator_labels } =
      cct_calculator_data;

    // Step values and selections
    const [currentStepNum, setCurrentStepNum] = useState(0);
    const [currentStepId, setCurrentStepId] = useState("intro");
    const [userJourney, setUserJourney] = useState([]);

    const [userSelections, setUserSelections] = useState({});

    // Calculator settings and values
    const [calculatorSettings, setCalculatorSettings] = useState({});
    const [calculatorValues, setCalculatorValues] = useState({});
    const [debouncedCalculatorValues, setDebouncedCalculatorValues] = useState({});

    // Data Layer Tracking
    const [dataTrackSteps, setDataTrackSteps] = useState([]);

    // Return comparison/calculation assumptions per user flow
    const dynamicResultsAssumptions = () => {
      if (userSelections["solution"] === "medicare" && userSelections["age_over_65"] === "yes") {
        return results_step_content["user_flows"]["medicare_over_65"]["assumptions"];
      }
      if (userSelections["solution"] === "medicare" && userSelections["age_over_65"] !== "yes") {
        return results_step_content["user_flows"]["medicare_under_65"]["assumptions"];
      }
      if (userSelections["solution"] === "insurance") {
        return results_step_content["user_flows"]["insurance"]["assumptions"];
      }
      if (userSelections["solution"] === "no_insurance") {
        return results_step_content["user_flows"]["no_insurance"]["assumptions"];
      }
      if (userSelections["solution"] === "other_cost_sharing_service") {
        return results_step_content["user_flows"]["other_cost_sharing_service"]["assumptions"];
      }
      return false;
    };

    // don't immediately calculate after each calculator input keystroke
    useEffect(() => {
      const timeoutId = setTimeout(() => {
        setDebouncedCalculatorValues(calculatorValues);
      }, 500);
      return () => clearTimeout(timeoutId);
    }, [calculatorValues]);

    // Populate calculator input settings and default values
    function resetCalculator(selections) {
      const people = Number(selections["people"]) || 1; // "people" value to populate initial settings/values
      const solution = selections["solution"] || "insurance"; // "solution" to populate initial settings/values

      // Make sure preset values exist.
      if (calculator_initial_settings[solution] && calculator_initial_settings[solution][people]) {
        setCalculatorSettings(calculator_initial_settings[solution][people]);
        setCalculatorValues(calculator_initial_values[solution][people]);
      } else if (calculator_initial_settings[solution]) {
        // If values don't exist for specified number of people, default to values for highest existing people added.
        const peopleEntries = calculator_initial_values[solution].length - 1;
        setCalculatorSettings(calculator_initial_settings[solution][peopleEntries]);
        setCalculatorValues(calculator_initial_values[solution][peopleEntries]);
      } else {
        // If no solution calculator has been created, return empty object.
        setCalculatorSettings({});
        setCalculatorValues({});
      }
    }

    // Determine next step based on user selections
    function determineNextStep(currentStepId) {
      if (currentStepId == "intro") {
        return "self";
      }
      if (currentStepId == "self") {
        return "solution";
      }
      if (currentStepId == "solution") {
        // Medicare over 65, skip risk tolerance and calculator. Only option is SeniorShare.
        if (userSelections["solution"] === "medicare" && userSelections["age_over_65"] === "yes") {
          return "interim";
        }
        return "risk_tolerance";
      }
      if (currentStepId == "risk_tolerance") {
        // skip calculator
        if (userSelections["solution"] === "medicare") {
          return "results";
        }
        // skip calculator
        if (userSelections["solution"] === "no_insurance") {
          return "results";
        }
      }
      if (currentStepId == "interim") {
        return "results";
      }
      if (currentStepId == "calculator") {
        return "results";
      }
      return "calculator";
    }

    // Ensure an option has been selected from each input group
    function confirmSelectedOption(container) {
      let numOptionsSelected = 0;
      // select card options
      const radioGroupOptions = container.querySelectorAll(".cost-comparison-tool__options");
      // make sure an option has been selected from each group
      radioGroupOptions.forEach((group) => {
        const checkedOption = group.querySelectorAll("input:checked");
        if (checkedOption.length > 0) {
          numOptionsSelected++;
          group.querySelector(".cost-comparison-tool__field-error-message").innerHTML = "";
        } else {
          group.querySelector(".cost-comparison-tool__field-error-message").innerHTML = "Please make a selection";
        }
      });
      return numOptionsSelected === radioGroupOptions.length;
    }

    // resize step card container for each step
    useEffect(() => {
      const container = $(".cost-comparison-tool__wrapper");
      const nextCard = $(`#${currentStepId}`);
      const cardHeight = $(nextCard).outerHeight(true);
      const progressBarHeight = $("#cct-progress-bar").outerHeight(true) || 0;
      $(container).css("min-height", cardHeight + progressBarHeight);
    }, [currentStepId]);

    function handleStepNextClick(e) {
      e.preventDefault();
      const currentCard = e.target.closest(".cost-comparison-tool__card");
      const allOptionsSelected = confirmSelectedOption(currentCard);
      const radioGroupOptions = currentCard.querySelectorAll(".cost-comparison-tool__options");

      // If option selected, move on to next step
      if (allOptionsSelected) {
        let dataObject = {};
        if (currentStepId === "calculator") {
          dataObject = {
            tool_name: "cost_comparison",
            event: "tool_engagement",
            engagement_type: "calculate_click",
            step_name: currentStepId,
            ...calculatorValues,
          };
        } else {
          dataObject = {
            tool_name: "cost_comparison",
            event: "tool_engagement",
            engagement_type: "next_click",
            step_name: currentStepId,
            ...userSelections,
          };
        }

        // push data object on first time through only
        if (!dataTrackSteps[currentStepId]) {
          dataLayer.push(dataObject);
          const nextDataTrackSteps = dataTrackSteps;
          nextDataTrackSteps[currentStepId] = 1;
          setDataTrackSteps(nextDataTrackSteps);
        }

        $(currentCard).fadeOut(100, () => {
          const nextStepId = determineNextStep(currentStepId);
          const nextStepNum = currentStepNum + 1;
          setUserJourney([...userJourney, currentStepId]);
          setCurrentStepNum(nextStepNum);
          setCurrentStepId(nextStepId);

          const url = new URL(location);
          const state = { stepId: nextStepId, stepNumber: nextStepNum };
          history.pushState(state, "", url);
        });

        // scroll to top
        window.scroll({
          top: 0,
          left: 0,
          behavior: "smooth",
        });
      }
    }

    function handleStepBackClick(e) {
      e.preventDefault();
      history.back();

      dataLayer.push({
        tool_name: "cost_comparison",
        event: "tool_engagement",
        engagement_type: "back_click",
      });
    }

    // Reflect proper step state if browser navigation is used
    function popStateListener(event) {
      if (event.state.stepId) {
        setCurrentStepId(event.state.stepId);
        setCurrentStepNum(event.state.stepNumber);
      }
    }

    useEffect(() => {
      // DEBUG: preselect user flow
      // const debug_selections = {
      //   solution: "insurance",
      //   people: "3",
      //   risk_tolerance: "high",
      //   age_over_65: "no",
      // };
      // setUserSelections(debug_selections);
      // resetCalculator(debug_selections);

      // disable Hubspot chat initially.
      window.hsConversationsSettings = {
        loadImmediately: false,
      };

      // listen for browser history changes
      window.addEventListener("popstate", popStateListener);

      // track tool start event
      dataLayer.push({
        event: "tool_engagement",
        engagement_type: "tool_start",
        tool_name: "cost_comparison",
      });

      return () => {
        window.removeEventListener("popstate", popStateListener);
      };
    }, []);

    return (
      <>
        <div
          className={`cost-comparison-tool__wrapper ${
            currentStepId !== "intro" ? "cost-comparison-tool__wrapper--wide" : ""
          }`}
        >
          {currentStepId !== "intro" && (
            <a onClick={handleStepBackClick} className="cost-comparison-tool__back-button" href="#">
              <i className="fa-regular fa-chevron-left"></i> Back
            </a>
          )}
          {currentStepNum > 0 && !["interim", "results"].includes(currentStepId) && (
            <Stepper stepNumber={currentStepNum} numSteps={_C.NUM_STEPS_TOTAL} />
          )}
          {currentStepId === "intro" && (
            <Intro stepNumber={0} stepId={`intro`} handleNextClick={handleStepNextClick} stepContent={intro_content} />
          )}
          {Object.entries(step_content).map(
            ([id, content]) =>
              currentStepId === id && (
                <Step stepId={id} stepContent={content} handleNextClick={handleStepNextClick}>
                  {Object.values(content.step_questions).map((question) => (
                    <StepOptions
                      optionType={question.step_question_type}
                      options={cct_step_options[question.step_question_type]}
                      optionLayout={question.step_question_layout}
                      userSelections={userSelections}
                      setUserSelections={setUserSelections}
                      resetCalculator={resetCalculator}
                    />
                  ))}
                </Step>
              )
          )}
          {currentStepId === "calculator" && (
            <CalculatorStep
              stepContent={calculator_step_content}
              inputLabels={cct_calculator_labels}
              calculatorSettings={calculatorSettings}
              calculatorValues={calculatorValues}
              setCalculatorValues={setCalculatorValues}
              handleNextClick={handleStepNextClick}
              userSelections={userSelections}
            />
          )}
          {currentStepId === "results" && (
            <ResultsStep
              chmPlusUnitMonthlyCost={_C.CHM_PLUS_MONTHLY_COST_PER_UNIT}
              currencyFormat={_C.CURRENCY_FORMAT}
              inputLabels={cct_calculator_labels}
              userSelections={userSelections}
              calculatorSettings={calculatorSettings}
              calculatorValues={calculatorValues}
              setCalculatorValues={setCalculatorValues}
              results_step_content={results_step_content}
              debouncedCalculatorValues={debouncedCalculatorValues}
            />
          )}
        </div>
        {currentStepId === "results" && dynamicResultsAssumptions() && (
          <p>
            <small>
              <sup>*</sup>
              {dynamicResultsAssumptions()}
            </small>
          </p>
        )}
        {currentStepId === "results" && (
          <div id="results-form" className="hubspot-form__wrapper">
            <div className="hubspot-form__header">
              <h2 className="hubspot-form__title">{results_step_content["recommendations_form_title"]}</h2>
              <div className="hubspot-form__description">
                <p>{results_step_content["recommendations_form_text"]}</p>
              </div>
            </div>
            <div id="hubspotForm" className="hbspt-form"></div>
          </div>
        )}
      </>
    );
  }

  const root = createRoot(container);
  root.render(
    <StrictMode>
      <CostComparisonTool />
    </StrictMode>
  );
}
